import type { EditOp } from "@overlay/op-schema";

type Undo = () => void;

export type ApplyResult = {
  appliedIds: string[];
  failed: { id: string; reason: string }[];
  revert: () => void;
};

export function applyOps(ops: EditOp[], root: ParentNode = document): ApplyResult {
  const undos: Undo[] = [];
  const appliedIds: string[] = [];
  const failed: { id: string; reason: string }[] = [];

  for (const op of ops) {
    try {
      const el = root.querySelector(op.target.selector) as HTMLElement | null;
      if (!el) {
        failed.push({ id: op.id, reason: "selector-miss" });
        continue;
      }
      const undo = applyOne(op, el);
      undos.push(undo);
      appliedIds.push(op.id);
    } catch (err) {
      failed.push({ id: op.id, reason: (err as Error).message });
    }
  }

  return {
    appliedIds,
    failed,
    revert: () => {
      while (undos.length) undos.pop()?.();
    },
  };
}

function applyOne(op: EditOp, el: HTMLElement): Undo {
  switch (op.type) {
    case "text.set":
    case "text.rewrite": {
      const prev = el.textContent ?? "";
      el.textContent = op.after;
      return () => {
        el.textContent = prev;
      };
    }
    case "attr.set": {
      const had = el.hasAttribute(op.name);
      const prev = el.getAttribute(op.name);
      el.setAttribute(op.name, op.value);
      return () => {
        if (had && prev !== null) el.setAttribute(op.name, prev);
        else el.removeAttribute(op.name);
      };
    }
    case "attr.remove": {
      const had = el.hasAttribute(op.name);
      const prev = el.getAttribute(op.name);
      el.removeAttribute(op.name);
      return () => {
        if (had && prev !== null) el.setAttribute(op.name, prev);
      };
    }
    case "style.set": {
      const prev: Record<string, string> = {};
      for (const k of Object.keys(op.declarations)) {
        prev[k] = el.style.getPropertyValue(k);
      }
      for (const [k, v] of Object.entries(op.declarations)) {
        el.style.setProperty(k, v);
      }
      return () => {
        for (const [k, v] of Object.entries(prev)) {
          if (v) el.style.setProperty(k, v);
          else el.style.removeProperty(k);
        }
      };
    }
    case "class.add": {
      const had = el.classList.contains(op.className);
      el.classList.add(op.className);
      return () => {
        if (!had) el.classList.remove(op.className);
      };
    }
    case "class.remove": {
      const had = el.classList.contains(op.className);
      el.classList.remove(op.className);
      return () => {
        if (had) el.classList.add(op.className);
      };
    }
    case "asset.replace": {
      const attr = op.attr ?? "src";
      const prev = el.getAttribute(attr);
      el.setAttribute(attr, op.after);
      return () => {
        if (prev !== null) el.setAttribute(attr, prev);
        else el.removeAttribute(attr);
      };
    }
    case "element.remove": {
      const parent = el.parentNode;
      const next = el.nextSibling;
      if (!parent) throw new Error("no parent");
      parent.removeChild(el);
      return () => {
        if (next) parent.insertBefore(el, next);
        else parent.appendChild(el);
      };
    }
    case "element.insert": {
      const frag = document.createRange().createContextualFragment(op.html);
      const inserted = Array.from(frag.childNodes);
      switch (op.position ?? "after") {
        case "before":
          el.parentNode?.insertBefore(frag, el);
          break;
        case "after":
          el.parentNode?.insertBefore(frag, el.nextSibling);
          break;
        case "prepend":
          el.insertBefore(frag, el.firstChild);
          break;
        case "append":
          el.appendChild(frag);
          break;
      }
      return () => {
        for (const n of inserted) n.parentNode?.removeChild(n);
      };
    }
    case "html.replace": {
      const parent = el.parentNode;
      if (!parent) throw new Error("no parent");
      const anchor = el.nextSibling;
      const frag = document.createRange().createContextualFragment(op.html);
      const inserted = Array.from(frag.childNodes);
      parent.removeChild(el);
      if (anchor) parent.insertBefore(frag, anchor);
      else parent.appendChild(frag);
      return () => {
        for (const n of inserted) n.parentNode?.removeChild(n);
        if (anchor) parent.insertBefore(el, anchor);
        else parent.appendChild(el);
      };
    }
  }
}

export function stableSelectorFor(el: Element): string {
  if (el.id) return `#${CSS.escape(el.id)}`;
  const dataId = el.getAttribute("data-overlay-id");
  if (dataId) return `[data-overlay-id="${CSS.escape(dataId)}"]`;

  const parts: string[] = [];
  let cur: Element | null = el;
  while (cur && cur.nodeType === 1 && cur.tagName.toLowerCase() !== "html") {
    const node: Element = cur;
    let part = node.tagName.toLowerCase();
    if (node.id) {
      parts.unshift(`${part}#${CSS.escape(node.id)}`);
      break;
    }
    const parent: Element | null = node.parentElement;
    if (parent) {
      const sameTag = Array.from(parent.children).filter(
        (c: Element) => c.tagName === node.tagName,
      );
      if (sameTag.length > 1) {
        const idx = sameTag.indexOf(node) + 1;
        part += `:nth-of-type(${idx})`;
      }
    }
    parts.unshift(part);
    cur = parent;
  }
  return parts.join(" > ");
}

export function summarizeElement(el: Element): {
  tag: string;
  id: string | null;
  classes: string[];
  text: string;
  attrs: Record<string, string>;
  selector: string;
  outerPreview: string;
} {
  const attrs: Record<string, string> = {};
  for (const a of Array.from(el.attributes)) attrs[a.name] = a.value;
  const text = (el.textContent ?? "").trim().slice(0, 240);
  const outerPreview = el.outerHTML.slice(0, 1200);
  return {
    tag: el.tagName.toLowerCase(),
    id: el.id || null,
    classes: Array.from(el.classList),
    text,
    attrs,
    selector: stableSelectorFor(el),
    outerPreview,
  };
}
